import { useTabList } from '@react-aria/tabs'
import { useTabListState } from '@react-stately/tabs'
import Styles from './Tabs.module.scss'
import { useObjectRef } from 'utils/useObjectRef'
import { AriaTabListProps, SpectrumTabsProps } from '@react-types/tabs'
import { TabPanel } from './TabPanel'
import { Key, useCallback, useEffect, useRef, useState } from 'react'
import { Tab } from 'components/Tabs/Tab'
import { useScrolling } from 'react-use'

export const SwipeableTabs = (props: SpectrumTabsProps<HTMLDivElement>) => {
  const state = useTabListState(props as AriaTabListProps<HTMLDivElement>)
  const ref = useObjectRef<HTMLDivElement>()
  const { tabListProps } = useTabList(props as AriaTabListProps<HTMLDivElement>, state, ref)
  const tabsRef = useRef(new Map<Key, HTMLDivElement>())
  const contentRef = useRef(null)
  const isScrolling = useScrolling(contentRef)

  const [observer] = useState(
    () =>
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const tabKey = entry.target.getAttribute('data-tab')
              if (tabKey === null) {
                return
              }

              ref.current?.dispatchEvent(new CustomEvent('snappedTo', { detail: { key: tabKey } }))
            }
          })
        },
        { threshold: 1.0 },
      ),
  )

  useEffect(() => {
    if (contentRef.current && state.selectedItem?.index !== undefined) {
      observer.disconnect()
      const content = contentRef.current as HTMLDivElement
      content.scrollTo({ left: state.selectedItem.index * content.clientWidth, behavior: 'smooth' })
    }
  }, [state.selectedItem?.index, observer])

  const addRef = useCallback(
    (key: Key) => (node: HTMLDivElement | null) => {
      if (isScrolling) return
      if (tabsRef.current.has(key)) {
        const existingNode = tabsRef.current.get(key)
        if (existingNode) {
          tabsRef.current.delete(key)
        }
      }
      if (node) {
        tabsRef.current.set(key, node)
      }
    },
    [isScrolling],
  )

  useEffect(() => {
    if (isScrolling) {
      return
    }
    tabsRef.current.forEach((tab, key) => {
      observer.observe(tab)
    })
  }, [isScrolling, observer])

  useEffect(() => {
    return () => {
      observer.disconnect()
    }
  }, [observer])

  const snapCallback = useCallback(
    (event: CustomEvent) => {
      if (event.detail.key === state.selectedItem?.key) {
        return
      }
      !isScrolling && state.setSelectedKey(event.detail.key)
    },
    [state, isScrolling],
  )

  useEffect(() => {
    const currentRef = ref.current
    currentRef?.addEventListener('snappedTo', (event) => snapCallback(event as CustomEvent))

    return () => {
      currentRef?.removeEventListener('snappedTo', (event) => snapCallback(event as CustomEvent))
    }
  }, [snapCallback, ref])

  return (
    <>
      <div {...tabListProps} ref={ref} className={Styles.tabs}>
        {[...state.collection].map((item) => (
          <Tab key={item.key} item={item} state={state} />
        ))}
      </div>
      <div className={Styles.tabsContainer}>
        <div className={Styles.tabsContainerInner} ref={contentRef}>
          {[...state.collection].map((tab) => {
            if (tab.key === state.selectedKey) {
              return <TabPanel state={state} key={state.selectedItem?.key} ref={addRef(tab.key)} />
            }
            return (
              <div className={Styles.tabContent} key={tab.key} ref={addRef(tab.key)} data-tab={tab.key}>
                {tab.props.children}
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}
